import { useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  api,
  saveFile,
  type Company,
  type ViesCheck,
  type VatUeRow,
  type VatUeSummary,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, date as formatDate } from '../format';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';
import type { Panel } from './Shell';

/**
 * Informacja podsumowująca VAT-UE (E8-S4).
 *
 * Trzy tabele zamiast jednej, bo to trzy różne części informacji i różne transakcje: dostawy
 * towarów, nabycia towarów i świadczenie usług. Pod nimi stoi to, czego **nie** sprawdzamy —
 * numery w VIES i transakcje trójstronne — bo o tym trzeba wiedzieć przed wysłaniem, a nie po.
 */
export function VatUePanel({
  company,
  month,
  t,
  locale,
  editable,
  onGo,
}: {
  company: Company;
  month: string;
  t: Dictionary;
  locale: Locale;
  editable: boolean;
  onGo?: (panel: Panel) => void;
}) {
  const [summary, setSummary] = useState<VatUeSummary | null>(null);
  /* Ostatnie sprawdzenie każdego numeru — stoi przy pozycji, bo to jego dotyczy */
  const [checks, setChecks] = useState<ViesCheck[]>([]);
  const [viesNote, setViesNote] = useState<string | null>(null);
  const [error, setError] = useState<Failure | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setDownloadError(null);
    try {
      const [wynik, sprawdzenia] = await Promise.all([api.vatUe(company.id, month), api.viesChecks(company.id)]);
      setSummary(wynik);
      setChecks(sprawdzenia);
    } catch (failure) {
      setSummary(null);
      setError(failureOf(failure));
    }
  }, [company.id, month]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Ostatnie sprawdzenie tego numeru; numer poznajemy po parze kraj-numer, nie po kontrahencie. */
  function checkOf(row: VatUeRow): ViesCheck | undefined {
    return checks.find((check) => check.countryCode === row.countryCode && check.vatNumber === row.vatNumber);
  }

  function table(title: string, rows: VatUeRow[]) {
    if (rows.length === 0) return null;
    return (
      <>
        <header>
          <h5>{title}</h5>
        </header>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.vatUe.counterparty}</th>
                <th>{t.vatUe.country}</th>
                <th>{t.vatUe.vatNumber}</th>
                <th>{t.vatUe.vies}</th>
                <th className="n">{t.vatUe.amount}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const check = checkOf(row);
                return (
                  <tr key={`${row.countryCode}-${row.vatNumber}`}>
                    <td>{row.counterpartyName ?? '—'}</td>
                    <td className="mono">{row.countryCode}</td>
                    <td className="mono">{row.vatNumber}</td>
                    <td>
                      {/* Trzy stany, nie dwa: ważny, nieważny i „nie pytaliśmy" — to nie to samo */}
                      {check === undefined ? (
                        <span className="sm muted">{t.vatUe.viesUnknown}</span>
                      ) : (
                        <span className={check.valid ? 'chip st-ok' : 'chip st-warn'}>
                          <i />
                          {check.valid ? t.vatUe.viesValid : t.vatUe.viesInvalid} ·{' '}
                          {formatDate(check.checkedAt.slice(0, 10), locale)}
                        </span>
                      )}
                    </td>
                    <td className="n">{amount(row.amount, locale)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <div className="panel">
      <header>
        <h4>{t.vatUe.title}</h4>
      </header>
      <p className="sm muted">{t.vatUe.lead}</p>

      <ErrorAlert error={error} t={t} onGo={onGo} />
      {downloadError && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{downloadError}</b>
          </div>
        </div>
      )}

      {summary?.empty ? (
        <div className="empty">
          <Icon.Check />
          <b>{t.vatUe.none}</b>
          <p>{t.vatUe.noneHint}</p>
        </div>
      ) : (
        summary && (
          <>
            {table(t.vatUe.supplies, summary.supplies)}
            {table(t.vatUe.acquisitions, summary.acquisitions)}
            {table(t.vatUe.services, summary.services)}

            {/* Pozycja bez numeru VAT-UE nie wejdzie do pliku — mówimy o tym, zanim ktoś go pobierze */}
            {summary.problems.length > 0 && (
              <div className="alert a-warn">
                <Icon.Warn />
                <div>
                  <b>{t.vatUe.problems}</b>
                  <ul>
                    {summary.problems.map((problem) => (
                      <li key={`${problem.documentNumber}-${problem.reason}`}>
                        {problem.documentNumber ?? '—'}: {problem.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {viesNote && (
              <div className="alert a-warn">
                <Icon.Warn />
                <div>
                  <b>{viesNote}</b>
                </div>
              </div>
            )}

            {editable && (
              <div className="row">
                <button
                  type="button"
                  className="btn ghost"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    setViesNote(null);
                    api
                      .checkViesForPeriod(company.id, month)
                      .then((batch) => {
                        // Rejestr państwa członkowskiego bywa niedostępny; wtedy mówimy kogo sprawdzić ponownie
                        setViesNote(
                          batch.unavailable.length > 0
                            ? `${t.vatUe.viesUnavailable} ${batch.unavailable.join(', ')}`
                            : null,
                        );
                        return load();
                      })
                      .catch((failure) => setError(failureOf(failure)))
                      .finally(() => setBusy(false));
                  }}
                >
                  {t.vatUe.checkVies}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={busy || summary.problems.length > 0}
                  onClick={() => {
                    setBusy(true);
                    setDownloadError(null);
                    api
                      .vatUeXml(company.id, month)
                      .then(saveFile)
                      .catch((failure) =>
                        setDownloadError(failure instanceof ApiError ? failure.message : String(failure)),
                      )
                      .finally(() => setBusy(false));
                  }}
                >
                  <Icon.Download />
                  {t.vatUe.download}
                </button>
              </div>
            )}
          </>
        )
      )}

      {/* Czego nie sprawdzamy — zdaniami z serwera, nie skrótem */}
      {summary?.notes.map((note) => (
        <p className="note" key={note}>
          {t.vatUe.notes[note]}
        </p>
      ))}
    </div>
  );
}
